import React, { useState, useEffect } from 'react';
import { useSearchParams } from '../utils/SimpleRouter';

const ScreensaverPreview = () => {
  const [searchParams] = useSearchParams();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [clockPosition, setClockPosition] = useState({ x: 100, y: 100 });
  const [clockVelocity, setClockVelocity] = useState({ x: 2, y: 2 });

  const clockType = searchParams.get('clockType') || 'digital';
  const bgImage = searchParams.get('bgImage') || 'AUTO_DETECT';
  const opacity = parseFloat(searchParams.get('opacity') || '0.5');
  const bgColor = searchParams.get('bgColor') || '#000000';

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Bouncing clock animation
  useEffect(() => {
    // Initialize position within safe bounds
    const clockSize = 350; // Safe padding
    const safeX = Math.max(50, Math.min(window.innerWidth - clockSize, 100));
    const safeY = Math.max(50, Math.min(window.innerHeight - clockSize, 100));
    
    setClockPosition({ x: safeX, y: safeY });
    
    const animateClock = () => {
      setClockPosition(prev => {
        let newX = prev.x + clockVelocity.x;
        let newY = prev.y + clockVelocity.y;
        let newVelX = clockVelocity.x;
        let newVelY = clockVelocity.y;
        
        const maxX = window.innerWidth - clockSize;
        const maxY = window.innerHeight - clockSize;
        
        // Bounce on edges with proper boundary clamping
        if (newX <= 0) {
          newVelX = Math.abs(newVelX);
          newX = 0;
        } else if (newX >= maxX) {
          newVelX = -Math.abs(newVelX);
          newX = maxX;
        }
        
        if (newY <= 0) {
          newVelY = Math.abs(newVelY);
          newY = 0;
        } else if (newY >= maxY) {
          newVelY = -Math.abs(newVelY);
          newY = maxY;
        }
        
        setClockVelocity({ x: newVelX, y: newVelY });
        return { x: newX, y: newY };
      });
    };
    
    const interval = setInterval(animateClock, 50);
    return () => clearInterval(interval);
  }, []);

  const getBackgroundUrl = () => {
    // Use same landscape image for all orientations - will be centered and fill height on portrait
    if (bgImage === 'AUTO_DETECT' || bgImage === 'DEFAULT_LANDSCAPE' || bgImage === 'DEFAULT_PORTRAIT') {
      return 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&h=1080&fit=crop';
    }
    return bgImage;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden',
      background: '#000'
    }}>
      {/* Background Image */}
      {getBackgroundUrl() && (
        <img
          src={getBackgroundUrl()}
          alt="Screensaver background"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      )}
      
      {/* Dark Overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: bgColor,
        opacity: opacity
      }}></div>

      {/* Bouncing Clock */}
      <div style={{
        position: 'absolute',
        left: `${clockPosition.x}px`,
        top: `${clockPosition.y}px`,
        transition: 'all 0.05s linear',
        color: 'white',
        textAlign: 'center',
        pointerEvents: 'none'
      }}>
        {clockType === 'analog' ? (
          // Analog Clock (SVG) - 150% larger
          <svg width="300" height="300" viewBox="0 0 200 200">
            <circle cx="100" cy="100" r="90" fill="rgba(255,255,255,0.1)" stroke="white" strokeWidth="4"/>
            {/* Hour markers */}
            {[...Array(12)].map((_, i) => {
              const angle = (i * 30 - 90) * (Math.PI / 180);
              const x1 = 100 + 75 * Math.cos(angle);
              const y1 = 100 + 75 * Math.sin(angle);
              const x2 = 100 + 85 * Math.cos(angle);
              const y2 = 100 + 85 * Math.sin(angle);
              return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="white" strokeWidth="3"/>;
            })}
            {/* Hour hand */}
            <line
              x1="100"
              y1="100"
              x2={100 + 40 * Math.sin((currentTime.getHours() % 12 + currentTime.getMinutes() / 60) * 30 * (Math.PI / 180))}
              y2={100 - 40 * Math.cos((currentTime.getHours() % 12 + currentTime.getMinutes() / 60) * 30 * (Math.PI / 180))}
              stroke="white"
              strokeWidth="6"
              strokeLinecap="round"
            />
            {/* Minute hand */}
            <line
              x1="100"
              y1="100"
              x2={100 + 60 * Math.sin(currentTime.getMinutes() * 6 * (Math.PI / 180))}
              y2={100 - 60 * Math.cos(currentTime.getMinutes() * 6 * (Math.PI / 180))}
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
            />
            {/* Second hand */}
            <line
              x1="100"
              y1="100"
              x2={100 + 70 * Math.sin(currentTime.getSeconds() * 6 * (Math.PI / 180))}
              y2={100 - 70 * Math.cos(currentTime.getSeconds() * 6 * (Math.PI / 180))}
              stroke="#50E3C2"
              strokeWidth="2"
              strokeLinecap="round"
            />
            {/* Center dot */}
            <circle cx="100" cy="100" r="5" fill="white"/>
          </svg>
        ) : (
          // Digital Clock - 144px (60% larger than original)
          <div style={{
            fontSize: '144px',
            fontFamily: "'Orbitron', monospace",
            fontWeight: 'bold',
            textShadow: '0 0 20px rgba(80, 227, 194, 0.5), 0 0 40px rgba(80, 227, 194, 0.3)',
            letterSpacing: '0.1em'
          }}>
            {currentTime.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>

      {/* Close button */}
      <button
        onClick={() => window.close()}
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '0.75rem 1.5rem',
          background: 'rgba(239, 68, 68, 0.9)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontWeight: '500',
          fontSize: '1rem',
          zIndex: 10001
        }}
      >
        ✕ Lukk forhåndsvisning
      </button>
    </div>
  );
};

export default ScreensaverPreview;
